import { getSearchResults } from "../../api"
import {
	createMarkup,
	Flex,
	HEADER_HEIGHT_S,
	Loader,
	Rte,
	scale,
	Text,
} from "@capioweb/ui-components/src"
import React from "react"
import { XhtmlString } from "../../@types/CapioWeb/core"
import { StringProperty } from "../../@types/EpiFoundationSpa/Property"
import {
	BreadcrumbsBlock,
	PaginationBlockDesktop,
	PaginationBlockMobile,
} from "../../components"
import { BreadcrumbItemProps, PageBaseProps } from "../../components/Core"
import {
	SiteSearchBlock,
	SiteSearchFilter,
	SiteSearchListing,
} from "./components"
import { SiteSearchFilterContextProvider } from "./context"
import { useSearchParams } from "react-router-dom"
import { useQuery } from "react-query"
import { queryStaleTime } from "../../constants"
import { SiteSearchProps } from "./@types/SiteSearchProps"
import { PaginationContextProvider } from "../../components/PaginationBlock/context/PaginationContext"
import { BlockCommonLayout } from "../../utils/layout"
import { AppContext, LocalesContext } from "../../context"
import { isSessionStorageAvailable, sendInternalSearchDataLayerPush } from "../../utils"

interface SiteSearchPageProps extends PageBaseProps {
	breadcrumb: Array<BreadcrumbItemProps>
	heading: StringProperty
	noResultsText: XhtmlString
}
interface NoResultProps {
	heading: StringProperty
	noResultsText: XhtmlString
}
const NoResult: React.FC<NoResultProps> = ({ heading, noResultsText }) => {
	const [searchParams] = useSearchParams()
	React.useEffect(() => {
		sendInternalSearchDataLayerPush(searchParams.get("query"), 0)
	}, [])
	return (
		<Flex flexDirection="column" w={1} m="auto">
			<SiteSearchBlock heading={heading} />
			<Flex mb={4} mt={2} mx="auto">
				<Rte>
					<div
						dangerouslySetInnerHTML={createMarkup(noResultsText.value)}
					></div>
				</Rte>
			</Flex>
		</Flex>
	)
}

const SiteSearchPage: React.FC<SiteSearchPageProps> = ({
	breadcrumb,
	heading,
	noResultsText,
}) => {
	const [searchParams] = useSearchParams()
	const { translate, translationKeys } = React.useContext(LocalesContext)
	const { userLocation } = React.useContext(AppContext)

	const showGeoPosition = isSessionStorageAvailable() ? sessionStorage.getItem("showMyPos") === "1" : false

	const { data, isLoading, isFetching } = useQuery<SiteSearchProps>(
		["search", userLocation],
		() =>
			getSearchResults(
				searchParams.get("query"),
				showGeoPosition ? userLocation?.coords?.longitude ?? null : null,
				showGeoPosition ? userLocation?.coords?.latitude ?? null : null
			),
		{
			staleTime: queryStaleTime,
		}
	)

	const ref = React.useRef(null)

	const scrollToRef = () => {
		window.scrollTo(0, ref.current.offsetTop)
		setTimeout(() => {
			const headerOffset = scale(HEADER_HEIGHT_S) + 10
			const elementPosition = ref.current.getBoundingClientRect().top
			const offsetPosition = elementPosition + window.scrollY - headerOffset

			window.scrollTo({
				top: offsetPosition,
				behavior: "smooth",
			})
		}, 100)
	}

	if (isLoading || isFetching)
		return (
			<Flex
				style={{ width: "100vw", height: "100vh" }}
				justifyContent="center"
				alignItems="center"
			>
				<Loader />
			</Flex>
		)
	return (
		<Flex display="block" w={1}>
			<BreadcrumbsBlock items={breadcrumb} />
			{!data || data.items.length <= 0 ? (
				<NoResult heading={heading} noResultsText={noResultsText} />
			) : (
				<>
					<Flex flexDirection="column" w={1} m="auto">
						<SiteSearchBlock heading={heading} query={data.query} />
						<Flex mb={4} mt={2} mx="auto">
							{data?.totalItems === 0 ? (
								<Rte>
									<div
										dangerouslySetInnerHTML={createMarkup(noResultsText.value)}
									></div>
								</Rte>
							) : (
								<Text fontSize={{ _: 1.75, m: 2 }} mb={0}>
									{translate(translationKeys.totalSearchResults).replace(
										/%s/g,
										(data.totalItems < 1000 ? data.totalItems : 1000).toString()
									)}
								</Text>
							)}
						</Flex>
					</Flex>

					<SiteSearchFilterContextProvider
						searchFilter={data?.pageFilterValues}
						items={data.items}
					>
						<PaginationContextProvider>
							<SiteSearchFilter pageFilterValues={data?.pageFilterValues} />
							<BlockCommonLayout>
								<Flex w={1} flexDirection="column" ref={ref}>
									<Text variant="headline-2" as="h2" mx="auto" mb={2}>
										{data.query}
									</Text>
									<SiteSearchListing items={data?.items} />
									<PaginationBlockDesktop onClickHandler={scrollToRef} />
									<PaginationBlockMobile onClickHandler={scrollToRef} />
								</Flex>
							</BlockCommonLayout>
						</PaginationContextProvider>
					</SiteSearchFilterContextProvider>
				</>
			)}
		</Flex>
	)
}

export { SiteSearchPage }
